import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, Toggle } from '../ui/BaseComponents';
import { EnhancedCodeBlock, MathRenderer } from '../ui/MathRenderer';
import TerminalOutput from '../terminalOutput';

/**
 * EditableQuestionCard - Editable question card for template editor
 * 
 * @param {Object} props
 * @param {Object} props.question - Question object
 * @param {number} props.index - Question index
 * @param {Function} props.onUpdate - Callback when question is updated
 * @param {Function} props.onDelete - Callback when question is deleted
 * @param {Function} props.onDuplicate - Callback when question is duplicated
 * @param {boolean} props.dragHandle - Whether to show drag handle
 */
function EditableQuestionCard({
  question,
  index,
  onUpdate,
  onDelete,
  onDuplicate,
  dragHandle = true
}) {
  const [isExpanded, setIsExpanded] = useState(question.isNew || false);
  const [isEditing, setIsEditing] = useState(question.isNew || false);
  const [editedQuestion, setEditedQuestion] = useState(question);

  // Sync editedQuestion when question prop changes (e.g., after delete shifts indices)
  useEffect(() => {
    if (!isEditing) {
      setEditedQuestion(question);
    }
  }, [question, isEditing]);

  const handleSave = () => {
    const { isNew, ...questionData } = editedQuestion;
    onUpdate(index, questionData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedQuestion(question);
    setIsEditing(false);
  };

  const handleAddOption = () => {
    setEditedQuestion({
      ...editedQuestion,
      options: [...editedQuestion.options, '']
    });
  };

  const handleRemoveOption = (optionIndex) => {
    if (editedQuestion.options.length <= 2) return; // Minimum 2 options

    const newOptions = editedQuestion.options.filter((_, i) => i !== optionIndex);
    
    // Adjust correct answers array: remove deleted index and adjust higher indices
    // Use correctAnswerIndices as the standard field
    const currentCorrect = editedQuestion.correctAnswerIndices || [0];
    const newCorrectAnswers = currentCorrect
      .filter(idx => idx !== optionIndex) // Remove if it was correct
      .map(idx => idx > optionIndex ? idx - 1 : idx); // Adjust indices
    
    // Ensure at least one correct answer
    const finalCorrectAnswers = newCorrectAnswers.length > 0 ? newCorrectAnswers : [0];

    setEditedQuestion({
      ...editedQuestion,
      options: newOptions,
      correctAnswerIndices: finalCorrectAnswers
    });
  };

  const handleOptionChange = (optionIndex, value) => {
    const newOptions = [...editedQuestion.options];
    newOptions[optionIndex] = value;
    setEditedQuestion({
      ...editedQuestion,
      options: newOptions
    });
  };

  // Collapsed View
  if (!isExpanded && !isEditing) {
    return (
      <Card
        padding="none"
        className="border-l-4 border-primary-400 hover:shadow-md transition-shadow"
      >
        <div className="flex items-center p-4">
          {dragHandle && (
            <div className="mr-3 cursor-move text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z" />
              </svg>
            </div>
          )}

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="primary" size="sm" className="text-xs">Frage {index + 1}</Badge>
              {(() => {
                const correctIndices = question.correctAnswerIndices || [0];
                if (correctIndices.length > 1) {
                  return <Badge variant="warning" size="sm">Multiple</Badge>;
                }
                return null;
              })()}
              {question.codeBlock?.code && (
                <Badge variant="default" size="sm">
                  <svg className="w-3 h-3 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                  {question.codeBlock.language}
                </Badge>
              )}
            </div>
            <div className="text-gray-800 dark:text-gray-200 font-medium line-clamp-2">
              <MathRenderer text={question.question || 'Unbenannte Frage'} />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {question.options.length} Optionen · Richtig: {(() => {
                const correctIndices = question.correctAnswerIndices || [0];
                return correctIndices.map(idx => String.fromCharCode(65 + idx)).join(', ');
              })()}
            </p>
          </div>

          <div className="flex items-center gap-2 ml-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm('Möchtest du diese Frage wirklich löschen?')) {
                  onDelete(index);
                }
              }}
              title="Löschen"
              className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(true);
              }}
              title="Ausklappen"
            >
              ▼
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  // Expanded/Editing View
  return (
    <Card padding="none" className="border-l-4 border-primary-400">
      {/* Header */}
      <div className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {dragHandle && !isEditing && (
            <div className="mr-2 cursor-move text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z" />
              </svg>
            </div>
          )}
          <Badge variant="primary">Frage {index + 1}</Badge>
          {isEditing && <Badge variant="warning">Bearbeitung</Badge>}
        </div>

        <div className="flex items-center gap-2">
          {!isEditing ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsEditing(true)}
                className="text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/30"
              >
                <svg className="w-4 h-4 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Bearbeiten
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDuplicate(index)}
              >
                <svg className="w-4 h-4 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Duplizieren
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(index)}
              >
                <svg className="w-4 h-4 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Löschen
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsExpanded(false)}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
              </Button>
            </>
          ) : (
            <>
              <Button variant="success" size="sm" onClick={handleSave}>
                <svg className="w-4 h-4 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Speichern
              </Button>
              <Button variant="secondary" size="sm" onClick={handleCancel}>
                <svg className="w-4 h-4 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Abbrechen
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="p-4 space-y-4">
        {/* Question Text */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Fragetext
          </label>
          {isEditing ? (
            <textarea
              value={editedQuestion.question}
              onChange={(e) => setEditedQuestion({ ...editedQuestion, question: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
              rows={3}
              placeholder="Gib deine Frage ein..."
            />
          ) : (
            <div className="text-gray-800 dark:text-gray-200 font-medium px-3 py-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <MathRenderer text={question.question || 'Unbenannte Frage'} />
            </div>
          )}
        </div>

        {/* Code Block - only in view mode */}
        {!isEditing && question.codeBlock && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Code
            </label>
            <EnhancedCodeBlock 
              language={question.codeBlock.language} 
              code={question.codeBlock.code} 
            />
          </div>
        )}

        {/* Terminal Output - only in view mode */}
        {!isEditing && question.terminalOutput && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Terminal-Ausgabe
            </label>
            <TerminalOutput output={question.terminalOutput} />
          </div>
        )}

        {/* Options */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Antwortmöglichkeiten
          </label>
          <div className="space-y-2">
            {(isEditing ? editedQuestion.options : question.options).map((option, optionIndex) => (
              <div key={optionIndex} className="flex items-center gap-2">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 font-bold flex items-center justify-center">
                  {String.fromCharCode(65 + optionIndex)}
                </div>

                {isEditing ? (
                  <>
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => handleOptionChange(optionIndex, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      placeholder={`Option ${String.fromCharCode(65 + optionIndex)}`}
                    />
                    <input
                      type="checkbox"
                      checked={(() => {
                        const correctIndices = editedQuestion.correctAnswerIndices || [0];
                        return correctIndices.includes(optionIndex);
                      })()}
                      onChange={(e) => {
                        const correctIndices = editedQuestion.correctAnswerIndices || [0];
                        const newCorrectIndices = e.target.checked
                          ? [...correctIndices, optionIndex]
                          : correctIndices.filter(idx => idx !== optionIndex);
                        // Ensure at least one correct answer
                        if (newCorrectIndices.length === 0) return;
                        setEditedQuestion({ 
                          ...editedQuestion, 
                          correctAnswerIndices: newCorrectIndices
                        });
                      }}
                      className="w-5 h-5 text-green-500 rounded"
                      title="Als richtig markieren"
                    />
                    {editedQuestion.options.length > 2 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveOption(optionIndex)}
                      >
                        ✕
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    <div className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg text-gray-800 dark:text-gray-200">
                      <MathRenderer text={option} />
                    </div>
                    {(() => {
                      const correctIndices = question.correctAnswerIndices || [0];
                      if (correctIndices.includes(optionIndex)) {
                        return <Badge variant="success" size="sm">✓ Correct</Badge>;
                      }
                      return null;
                    })()}
                  </>
                )}
              </div>
            ))}
          </div>

          {isEditing && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleAddOption}
              className="mt-2"
              fullWidth
            >
              + Option hinzufügen
            </Button>
          )}
        </div>

        {/* Explanation - only in view mode, shown after options */}
        {!isEditing && question.explanation && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Erklärung
            </label>
            <div className="text-gray-600 dark:text-gray-400 px-3 py-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg text-sm">
              <MathRenderer text={question.explanation} />
            </div>
          </div>
        )}

        {/* Advanced Options */}
        {isEditing && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-3">
            {/* Multiple correct answers are now always supported via checkboxes */}

            {/* Code Block Toggle */}
            <div>
              <Toggle
                checked={!!editedQuestion.codeBlock}
                onChange={(checked) => {
                  if (checked) {
                    setEditedQuestion({
                      ...editedQuestion,
                      codeBlock: { language: 'javascript', code: '' }
                    });
                  } else {
                    const { codeBlock, ...rest } = editedQuestion;
                    setEditedQuestion(rest);
                  }
                }}
                label="Codeblock einfügen"
              />

              {editedQuestion.codeBlock && (
                <div className="mt-2 space-y-2">
                  <select
                    value={editedQuestion.codeBlock.language}
                    onChange={(e) => setEditedQuestion({
                      ...editedQuestion,
                      codeBlock: { ...editedQuestion.codeBlock, language: e.target.value }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="javascript">JavaScript</option>
                    <option value="python">Python</option>
                    <option value="java">Java</option>
                    <option value="cpp">C++</option>
                    <option value="html">HTML</option>
                    <option value="css">CSS</option>
                    <option value="sql">SQL</option>
                  </select>
                  <textarea
                    value={editedQuestion.codeBlock.code}
                    onChange={(e) => setEditedQuestion({
                      ...editedQuestion,
                      codeBlock: { ...editedQuestion.codeBlock, code: e.target.value }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-sm bg-gray-900 text-gray-100"
                    rows={5}
                    placeholder="// Code hier eingeben..."
                  />
                </div>
              )}
            </div>

            {/* Terminal Output Toggle */}
            <div>
              <Toggle
                checked={editedQuestion.terminalOutput !== undefined && editedQuestion.terminalOutput !== null}
                onChange={(checked) => {
                  if (checked) {
                    setEditedQuestion({ ...editedQuestion, terminalOutput: '' });
                  } else {
                    const { terminalOutput, ...rest } = editedQuestion;
                    setEditedQuestion(rest);
                  }
                }}
                label="Terminal-Ausgabe einfügen"
              />

              {(editedQuestion.terminalOutput !== undefined && editedQuestion.terminalOutput !== null) && (
                <textarea
                  value={editedQuestion.terminalOutput}
                  onChange={(e) => setEditedQuestion({ ...editedQuestion, terminalOutput: e.target.value })}
                  className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm bg-gray-900 text-green-400"
                  rows={4}
                  placeholder="$ Befehlsausgabe..."
                />
              )}
            </div>

            {/* Explanation Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Erklärung (optional)
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                Diese Erklärung wird nach Beantwortung im Übungsmodus angezeigt.
              </p>
              <textarea
                value={editedQuestion.explanation || ''}
                onChange={(e) => setEditedQuestion({ ...editedQuestion, explanation: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                rows={3}
                placeholder="Erkläre, warum diese Antwort richtig ist..."
              />
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

export default EditableQuestionCard;
